import { type AccessNote, accessChoices, type GroupRole } from '@echo/core';
import { useGroupPeople, useSetGroupRole } from '@echo/core/react';
import { Button } from '@echo/ui-web';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useId, useState } from 'react';
import { AccessChoice, useChoiceLabel, useRoleDescriptions } from '@/components/access-choice';
import { ToolbarBack } from '@/components/empty-state-frame';
import { GroupActionFailed, GroupLoadFailed, GroupLoading } from '@/components/group-load-state';
import { InfoIcon } from '@/components/info-icon';
import { useGoBack } from '@/lib/use-go-back';

export const Route = createFileRoute('/_app/account_/groups_/$id_/member/$personId_/access')({
  component: AccessLevelPage,
});

/**
 * Member profile › Access level (Figma account/access-level and "Manage own access"): the roles
 * this person may have, theirs selected. For someone else: Member or Group leader — nobody makes
 * someone else a Facilitator. For you: Facilitator when you are one, Member only once someone
 * else leads the group. Save gives them that role in Core, which never takes Admin from the
 * group's last holder.
 */
function AccessLevelPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const description = useRoleDescriptions();
  const label = useChoiceLabel();
  const promptId = useId();
  const { id, personId } = Route.useParams();
  const people = useGroupPeople(id);
  const setRole = useSetGroupRole(id);
  const person = people.data?.find((candidate) => candidate.id === personId);
  const [picked, setChosen] = useState<GroupRole | undefined>();
  const goBack = useGoBack(
    () =>
      void navigate({
        to: '/account/groups/$id/member/$personId',
        params: { id, personId },
        replace: true,
      }),
  );
  const toolbar = (
    <ToolbarBack
      render={({ className, children }) => (
        <button type="button" className={className} onClick={goBack}>
          {children}
        </button>
      )}
    />
  );
  const heading = (
    <h1 className="text-display-sm font-bold">
      <Trans>Manage access</Trans>
    </h1>
  );
  if (people.isPending) {
    return (
      <>
        {toolbar}
        {heading}
        <GroupLoading />
      </>
    );
  }
  if (people.isError) {
    return (
      <>
        {toolbar}
        {heading}
        <div className="pt-content-md">
          <GroupLoadFailed onRetry={() => void people.refetch()} />
        </div>
      </>
    );
  }
  // Removed (on its way out) or an unknown id from a link.
  if (!person) return toolbar;

  const chosen = picked ?? person.role;
  const { choices, note } = accessChoices(people.data, person.id);
  const save = () => {
    if (setRole.isPending) return;
    if (chosen === person.role) return goBack();
    setRole.mutate({ personId: person.id, role: chosen }, { onSuccess: goBack });
  };
  const noteText: Record<AccessNote, string> = {
    'facilitator-again': t`You can choose to be a Facilitator again any time from Manage Group.`,
    'needs-other-leader': t`You need to assign someone else as Leader before you can become a member.`,
    'cannot-make-facilitator': t`You can't make someone a Facilitator. Leaders can choose this role themselves in their own Account settings.`,
  };

  return (
    <>
      {toolbar}
      {heading}
      <div className="flex flex-col gap-section-sm pt-content-md">
        <div
          role="radiogroup"
          aria-labelledby={promptId}
          className="flex flex-col gap-content-sm rounded-xl border border-border-weak bg-surface p-content-sm"
        >
          <p id={promptId} className="text-body-md">
            {person.you ? (
              <Trans>Choose what you can do in this group.</Trans>
            ) : (
              <Trans>Choose what this person can do in the group.</Trans>
            )}
          </p>
          <div className="flex flex-col">
            {choices.map((choice) => (
              <AccessChoice
                key={choice.role}
                name={promptId}
                label={label(choice.role, choice.current)}
                description={description[choice.role]}
                selected={chosen === choice.role}
                disabled={choice.disabled}
                onPress={() => setChosen(choice.role)}
              />
            ))}
          </div>
        </div>
        {note ? (
          <div className="flex gap-internal-md">
            <InfoIcon />
            <p className="flex-1 text-body-md text-on-surface-medium">{noteText[note]}</p>
          </div>
        ) : null}
        {setRole.isError ? <GroupActionFailed /> : null}
        <div className="flex flex-wrap justify-end gap-content-sm">
          <Button variant="ghost" onClick={goBack}>
            {t`Cancel`}
          </Button>
          <Button onClick={save} loading={setRole.isPending}>
            {t`Save`}
          </Button>
        </div>
      </div>
    </>
  );
}
